import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  alpha,
  Chip,
  Button,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // LAP-733 (GE022) round 10 fix-up
import { useSubscription } from './SubscriptionContext';
import { goToButtonTarget } from './publicationHandlers';
import { getTranslation, getUserLanguage } from './translations'; // LAP-733 (GE022) round 10 fix-up

// LAP-733 (GE022) round 10 fix-up — server stores type as 'Insight'|'Summary'|
// 'Reminder'|'Actionable'|'News'|'Publication'; map each to a translation key.
const PUBLICATION_TYPE_KEY = {
  Insight: 'publication',
  Publication: 'publication',
  Summary: 'summary',
  Reminder: 'reminder',
  Actionable: 'actionable',
  News: 'news',
};

const NewsCard = ({ publication, onInteraction, showArchivedIndicators = false }) => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [isRead, setIsRead] = useState(publication.is_read || false);
  const [imageError, setImageError] = useState(false);

  // LAP-733 (GE022) round 10 fix-up — resolve user language + BCP-47 locale
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  const dateLocale = ({ fr: 'fr-FR', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES' })[lang] || lang || 'fr-FR';

  // LAP-733 (GE022) round 10 fix-up — translate the publication.type badge
  const displayType = getTranslation(PUBLICATION_TYPE_KEY[publication.type] || 'publication', lang);
  
  // Debug: Log image URL
  useEffect(() => {
    if (publication.image_url) {
      console.log('NewsCard - Image URL:', publication.image_url);
      console.log('NewsCard - Publication ID:', publication.id);
    }
  }, [publication.image_url, publication.id]);

  const handleRead = () => {
    if (!isRead) {
      setIsRead(true);
      onInteraction(publication.id, 'read');
    }
  };

  const handleLike = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'like');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'save');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'dismiss');
  };

  const handleExternalLink = (e) => {
    e.stopPropagation();
    if (publication.external_url) {
      window.open(publication.external_url, '_blank', 'noopener,noreferrer');
      handleRead();
    }
  };

  const linkTarget = publication.button_target || publication.external_url;
  const hasLinkButton = (publication.button_text || publication.button_target || publication.external_url) && !!linkTarget;

  const handleLinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Open link first (same user gesture) so window.open isn't blocked by the browser
    goToButtonTarget(linkTarget, navigate, publication);
    onInteraction(publication.id, 'click');
    if (!isRead && publication.external_url) handleRead();
  };

  const newsColor = '#06b6d4'; // Cyan
  const newsGradient = 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';

  return (
    <Card
      onClick={handleRead}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1a1d2e',
        borderRadius: '16px',
        border: `1px solid ${alpha(newsColor, 0.3)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        opacity: isRead ? 0.8 : 1,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 25px -5px ${alpha(newsColor, 0.4)}, 0 10px 10px -5px ${alpha(
            newsColor,
            0.3,
          )}`,
          borderColor: alpha(newsColor, 0.5),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: newsGradient,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="start" gap={1} flexGrow={1}>
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontSize: '2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {publication.icon || '📰'}
            </Typography>

            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.4,
                    color: '#E0E0E0',
                  }}
                >
                  {publication.title}
                </Typography>
                {!isRead && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: newsColor,
                    }}
                  />
                )}
              </Box>

              {publication.subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#E0E0E0', 0.7),
                    fontSize: '0.85rem',
                    mb: 1,
                  }}
                >
                  {publication.subtitle}
                </Typography>
              )}

              {/* Source Attribution */}
              {publication.source && (
                <Chip
                  label={publication.source}
                  size="small"
                  sx={{
                    bgcolor: alpha(newsColor, 0.2),
                    color: newsColor,
                    fontSize: '0.7rem',
                    height: '20px',
                  }}
                />
              )}
            </Box>
          </Box>
          
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: '#ef4444',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Image */}
        {publication.image_url && !imageError && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 2,
              mt: 1,
              minHeight: 200,
              maxHeight: 300,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.05)', // Placeholder background
              position: 'relative',
            }}
          >
            <img
              src={publication.image_url}
              alt={publication.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
                maxHeight: '300px',
                minHeight: '200px',
              }}
              onError={(e) => {
                console.error('Failed to load image:', publication.image_url);
                console.error('Publication ID:', publication.id);
                setImageError(true);
                // Hide the image on error
                e.target.style.display = 'none';
              }}
              onLoad={() => {
                console.log('Image loaded successfully:', publication.image_url);
                setImageError(false);
              }}
              loading="lazy"
            />
          </Box>
        )}
        
        {/* Show error message if image failed to load */}
        {publication.image_url && imageError && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              mb: 2,
              mt: 1,
              minHeight: 200,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px dashed rgba(255,255,255,0.2)',
              p: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                textAlign: 'center',
              }}
            >
              Image non disponible
            </Typography>
          </Box>
        )}

        {/* Text Content */}
        {publication.text1 && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.9),
              fontSize: '0.9rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            {publication.text1}
          </Typography>
        )}

        {/* GPT-generated content indicator */}
        {publication.is_gpt_generated && (
          <Chip
            label="AI Generated"
            size="small"
            sx={{
              bgcolor: alpha('#8b5cf6', 0.2),
              color: '#8b5cf6',
              fontSize: '0.7rem',
              height: '20px',
              mb: 1,
            }}
          />
        )}

        {/* Action button when button_text, button_target, or external_url is defined */}
        {hasLinkButton && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleLinkClick}
            endIcon={<OpenInNewIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              mb: 2,
              borderColor: alpha(newsColor, 0.5),
              color: newsColor,
              textTransform: 'none',
              '&:hover': {
                borderColor: newsColor,
                bgcolor: alpha(newsColor, 0.1),
              },
            }}
          >
            {publication.button_text || 'Lire la suite'}
          </Button>
        )}

        {/* Interaction buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={handleLike}
              sx={{
                color: publication.was_liked ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              {publication.was_liked ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSave}
              sx={{
                color: publication.is_saved ? '#f59e0b' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                },
              }}
            >
              {publication.is_saved ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {/* Type Chip */}
            <Chip
              label={displayType}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
            {/* Show saved/liked indicators in archived view */}
            {showArchivedIndicators && (
              <>
                {publication.is_saved && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#8b5cf6', 0.2),
                      color: '#8b5cf6',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <BookmarkIcon sx={{ fontSize: '0.7rem' }} />
                    Saved
                  </Box>
                )}
                {publication.was_liked && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#ef4444', 0.2),
                      color: '#ef4444',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <FavoriteIcon sx={{ fontSize: '0.7rem' }} />
                    Liked
                  </Box>
                )}
              </>
            )}
            {publication.datetime_displayed && (
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#E0E0E0', 0.5),
                  fontSize: '0.75rem',
                }}
              >
                {new Date(publication.datetime_displayed).toLocaleDateString(dateLocale) /* LAP-733 (GE022) round 10 fix-up */}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewsCard;

